import React, { useEffect, useMemo, useState } from 'react'
import { BoxV2, ExternalLink, StatusChipV4 } from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquareSliders } from '@fortawesome/pro-regular-svg-icons'
import clsx from 'clsx'
import { useParams } from 'react-router-dom'
import { useFlagValue } from '@labrav/flags'
import { useSelector } from 'react-redux'
import { useTheme } from '@mui/material/styles'
import { useStyles } from './ConfigureLabraSalesforceAutomations.styles'
import Persona from '../Persona/Persona'
import { CloudTypeEnum as CloudType } from '../../types'
import { CRMTypes } from '../../utils/constants'
import {
  useOnboardingAccountId,
  useOnboardingCrmType,
} from '../../utils/hooks/useCrmOnboardingContext'
import { useUserType } from '../../utils/hooks/useUserType'
import {
  useSalesforceEventBridgeStatus,
  type UseSalesforceEventBridgeStatusResult,
} from '../../utils/hooks/useSalesforceEventBridgeStatus'
import { buildSalesforceLabraCanvasGuidedHref } from './buildSalesforceLabraCanvasGuidedHref'
import type { CrmOnboardingRootState } from '../../modules/crmSelector/crmOnboardingState'

export { buildSalesforceLabraCanvasGuidedHref }

type Props = {
  appLink: string
  /**
   * When provided (e.g. from CRMConfiguration), avoids a duplicate POST to
   * event-bridge/status — uses this data instead of fetching in this component.
   */
  prefetchedEventBridgeStatus?: UseSalesforceEventBridgeStatusResult
}

const ConfigureLabraSalesforceAutomations: React.FC<Props> = ({
  appLink,
  prefetchedEventBridgeStatus,
}) => {
  const { classes } = useStyles()
  const partnerType = useUserType()
  const crmId = useSelector(
    (state: CrmOnboardingRootState) =>
      state.AccountData?.[partnerType]?.partnerData?.crmId || ''
  )
  const theme = useTheme()
  const crmType = useOnboardingCrmType()
  const fetchAutomationStatusEnabled =
    crmType === CRMTypes.salesforce && !!crmId
  const eventBridgeFromHook = useSalesforceEventBridgeStatus({
    crmId,
    fetchEnabled:
      fetchAutomationStatusEnabled && prefetchedEventBridgeStatus === undefined,
    authMode: 'labra',
  })
  const bridge: UseSalesforceEventBridgeStatusResult =
    prefetchedEventBridgeStatus ?? eventBridgeFromHook

  const isEnabled = bridge.isRelayEnabled === true
  const { value: isSalesforceMappingManagerEnabled } = useFlagValue(
    'mappingManagerMultipleApnSalesforce'
  )

  const { accountId: routeAccountId, cloudType: routeCloudType } = useParams<{
    cloudType: string
    accountId: string
  }>()
  const [selectedCloud, setSelectedCloud] = useState<string>(
    isSalesforceMappingManagerEnabled &&
      crmType === CRMTypes.salesforce &&
      routeCloudType
      ? routeCloudType.toLowerCase()
      : 'aws'
  )

  const accountIdFromHook = useOnboardingAccountId()
  const accountId =
    isSalesforceMappingManagerEnabled &&
    crmType === CRMTypes.salesforce &&
    routeAccountId
      ? routeAccountId
      : accountIdFromHook
  const syncConfigurationPath = useMemo(() => {
    const cloud = (selectedCloud || 'aws').toLowerCase()
    if (!accountId) {
      return '/settings/mappings/co-sell'
    }
    return `/settings/${cloud}/${accountId}/mappings/co-sell`
  }, [accountId, selectedCloud])

  const salesforceGuidedHref = useMemo(
    () => buildSalesforceLabraCanvasGuidedHref(appLink, syncConfigurationPath),
    [appLink, syncConfigurationPath]
  )

  useEffect(() => {
    if (
      isSalesforceMappingManagerEnabled &&
      crmType === CRMTypes.salesforce &&
      routeCloudType
    ) {
      setSelectedCloud(routeCloudType.toLowerCase())
    }
  }, [routeCloudType, isSalesforceMappingManagerEnabled, crmType])

  return (
    <BoxV2
      heading="How to configure Labra and Salesforce automations"
      headingIcon={
        <FontAwesomeIcon
          icon={faSquareSliders}
          style={{ height: '20px', width: 14 }}
        />
      }
      actions={
        !isEnabled ? (
          <div data-testid="automation-persona">
            <Persona
              memberType="admin"
              cloudType={CloudType.AWS}
              displayLabel="Salesforce"
            />
          </div>
        ) : undefined
      }
    >
      <div>
        <StatusChipV4
          label={isEnabled ? 'Enabled successfully' : 'Not enabled'}
          variant={isEnabled ? 'success' : 'error'}
          size="small"
          bgColor={
            isEnabled
              ? theme.palette.avatarColors.lightGreen5
              : theme.palette.bgPrimary.dark
          }
          textColor={
            isEnabled
              ? theme.palette.syncStatus.success
              : theme.palette.syncStatus.error
          }
          borderColor={theme.palette.datePicker.background}
        />
      </div>

      <div
        data-testid="salesforce_automation_setup"
        className={clsx(classes.steps, isEnabled && classes.disabledSteps)}
      >
        <div className={classes.row}>
          <div className={classes.stepBubble}>Step 1</div>
          <span>Logout from Salesforce</span>
        </div>

        <div className={classes.row}>
          <div className={classes.stepBubble}>Step 2</div>
          <div className={classes.columns}>
            <div className={classes.lineText}>
              <span>
                Login to Salesforce admin, Open Salesforce Canvas to enable
                triggers via a guided link{' '}
                <ExternalLink
                  className={clsx(
                    classes.text,
                    classes.linkText,
                    isEnabled && classes.disabledLinkText
                  )}
                  href={salesforceGuidedHref}
                >
                  here
                </ExternalLink>
              </span>
            </div>
          </div>
        </div>
      </div>
    </BoxV2>
  )
}

export default ConfigureLabraSalesforceAutomations
